import * as moment from "moment";
import {AthleteSeason} from "../../models/athleteSeason";
import {Athlete} from "../../models/athlete";

export type EnrollmentStatus = 'paid' | 'verifyingPayment' | 'pendingPayment';

export const ENROLLMENT_STATUSES: {id: EnrollmentStatus, label: string, short: string}[] = [
    {id: 'paid', label: 'Pagato', short: 'Pagati'},
    {id: 'verifyingPayment', label: 'Verifica pagamento', short: 'Da verificare'},
    {id: 'pendingPayment', label: 'In attesa di pagamento', short: 'In attesa'},
];

export interface EnrollmentStats {
    total: number;
    paid: number;
    verifyingPayment: number;
    pendingPayment: number;
    withoutMembership: number;
    collected: number;
    expected: number;
}

/** The season can be 'paid' while its installment plan is still running: show that instead. */
export function enrollmentDisplayStatus(enrollment: AthleteSeason): string | undefined {
    return enrollment.athletePaymentRequest?.status === 'partialPayment' ? 'partialPayment' : enrollment.status;
}

export function enrollmentStatusLabel(status?: string): string {
    if (status === 'partialPayment') return 'Pagamento parziale';
    return ENROLLMENT_STATUSES.find(item => item.id === status)?.label ?? 'Sconosciuto';
}

export function enrollmentStatusIcon(status?: string): string {
    switch (status) {
        case 'paid': return 'bi-check-circle-fill';
        case 'verifyingPayment': return 'bi-hourglass-split';
        case 'partialPayment': return 'bi-pie-chart-fill';
        case 'pendingPayment': return 'bi-exclamation-circle-fill';
        default: return 'bi-question-circle';
    }
}

export function enrollmentAmount(enrollment: AthleteSeason): number {
    const amount = parseFloat(enrollment.athletePaymentRequest?.amount ?? '');
    return isNaN(amount) ? 0 : amount;
}

/** Latest membership badge expiration, or null when the athlete was never registered. */
export function membershipExpiration(enrollment: AthleteSeason): moment.Moment | null {
    return athleteMembershipExpiration(enrollment.athlete);
}

export function athleteMembershipExpiration(athlete?: Athlete): moment.Moment | null {
    const dates = (athlete?.memberships ?? [])
        .map(membership => membership.badgeExpiration ? moment(membership.badgeExpiration, 'YYYY-MM-DD HH:mm:ss') : null)
        .filter((date): date is moment.Moment => !!date && date.isValid())
        .sort((a, b) => b.valueOf() - a.valueOf());
    return dates[0] ?? null;
}

export function hasActiveMembership(enrollment: AthleteSeason): boolean {
    const expiration = membershipExpiration(enrollment);
    return !!expiration && expiration.isAfter(moment());
}

export function computeEnrollmentStats(enrollments: AthleteSeason[]): EnrollmentStats {
    const stats: EnrollmentStats = {
        total: enrollments.length, paid: 0, verifyingPayment: 0, pendingPayment: 0,
        withoutMembership: 0, collected: 0, expected: 0,
    };
    for (const enrollment of enrollments) {
        const amount = enrollmentAmount(enrollment);
        stats.expected += amount;
        if (enrollment.status === 'paid') stats.collected += amount;
        if (enrollment.status === 'paid' || enrollment.status === 'verifyingPayment' || enrollment.status === 'pendingPayment') {
            stats[enrollment.status]++;
        }
        if (!hasActiveMembership(enrollment)) stats.withoutMembership++;
    }
    return stats;
}

export function formatEuro(value: number): string {
    return (value ?? 0).toLocaleString('it-IT', {style: 'currency', currency: 'EUR', maximumFractionDigits: 0});
}

export function percent(part: number, total: number): number {
    return total ? Math.round(part / total * 100) : 0;
}
